import { createContext, ReactElement, ReactNode, useContext, useMemo, useState } from 'react'
import { PointAxisKey, PointManipulationConfig, RoundOnReplaceScope } from '../types'

interface AxisReplaceRoundingState {
  roundOnReplace: boolean
  numDecimalsRoundingOnReplace: number
}

type AxisState = Record<PointAxisKey, AxisReplaceRoundingState>

const getRoundOnReplaceScopeFromConfig = (config: PointManipulationConfig): RoundOnReplaceScope =>
  config.z.roundOnReplace ? 'xyz' : 'xy'

const applyRoundOnReplaceEnabled = (
  previousState: AxisState,
  enabled: boolean,
  scope: RoundOnReplaceScope,
): AxisState => ({
  x: { ...previousState.x, roundOnReplace: enabled },
  y: { ...previousState.y, roundOnReplace: enabled },
  z: { ...previousState.z, roundOnReplace: enabled && scope === 'xyz' },
})

interface ReplaceDecimalRoundingContextValue {
  axisState: AxisState
  roundOnReplaceScope: RoundOnReplaceScope
  setAxisRoundOnReplace: (axis: PointAxisKey, roundOnReplace: boolean) => void
  setAxisNumDecimalsRoundingOnReplace: (axis: PointAxisKey, numDecimals: number) => void
  setRoundOnReplaceEnabled: (roundOnReplace: boolean) => void
  setRoundOnReplaceScope: (scope: RoundOnReplaceScope) => void
  setAllAxesNumDecimalsRoundingOnReplace: (numDecimals: number) => void
  getAxisReplaceNumDecimals: (axis: PointAxisKey) => number | null
}

const ReplaceDecimalRoundingContext = createContext<ReplaceDecimalRoundingContextValue | null>(null)

const createAxisStateFromConfig = (config: PointManipulationConfig): AxisState => ({
  x: {
    roundOnReplace: config.x.roundOnReplace,
    numDecimalsRoundingOnReplace: config.x.defaultRoundNumDecimalsOnReplace,
  },
  y: {
    roundOnReplace: config.y.roundOnReplace,
    numDecimalsRoundingOnReplace: config.y.defaultRoundNumDecimalsOnReplace,
  },
  z: {
    roundOnReplace: config.z.roundOnReplace,
    numDecimalsRoundingOnReplace: config.z.defaultRoundNumDecimalsOnReplace,
  },
})

interface ReplaceDecimalRoundingProviderProps {
  pointManipulation: PointManipulationConfig
  children: ReactNode
}

export const ReplaceDecimalRoundingProvider = ({
  pointManipulation,
  children,
}: ReplaceDecimalRoundingProviderProps): ReactElement => {
  const [axisState, setAxisState] = useState(() => createAxisStateFromConfig(pointManipulation))
  const [roundOnReplaceScope, setRoundOnReplaceScopeState] = useState<RoundOnReplaceScope>(() =>
    getRoundOnReplaceScopeFromConfig(pointManipulation),
  )

  const value = useMemo(
    () => ({
      axisState,
      roundOnReplaceScope,
      setAxisRoundOnReplace: (axis: PointAxisKey, roundOnReplace: boolean) => {
        setAxisState(previousState => ({
          ...previousState,
          [axis]: { ...previousState[axis], roundOnReplace },
        }))
      },
      setAxisNumDecimalsRoundingOnReplace: (axis: PointAxisKey, numDecimals: number) => {
        setAxisState(previousState => ({
          ...previousState,
          [axis]: { ...previousState[axis], numDecimalsRoundingOnReplace: numDecimals },
        }))
      },
      setRoundOnReplaceEnabled: (roundOnReplace: boolean) => {
        setAxisState(previousState =>
          applyRoundOnReplaceEnabled(previousState, roundOnReplace, roundOnReplaceScope),
        )
      },
      setRoundOnReplaceScope: (scope: RoundOnReplaceScope) => {
        setRoundOnReplaceScopeState(scope)
        setAxisState(previousState => {
          const enabled = previousState.x.roundOnReplace || previousState.y.roundOnReplace
          return applyRoundOnReplaceEnabled(previousState, enabled, scope)
        })
      },
      setAllAxesNumDecimalsRoundingOnReplace: (numDecimals: number) => {
        setAxisState(previousState => ({
          x: { ...previousState.x, numDecimalsRoundingOnReplace: numDecimals },
          y: { ...previousState.y, numDecimalsRoundingOnReplace: numDecimals },
          z: { ...previousState.z, numDecimalsRoundingOnReplace: numDecimals },
        }))
      },
      getAxisReplaceNumDecimals: (axis: PointAxisKey) =>
        axisState[axis].roundOnReplace ? axisState[axis].numDecimalsRoundingOnReplace : null,
    }),
    [axisState, roundOnReplaceScope],
  )

  return (
    <ReplaceDecimalRoundingContext.Provider value={value}>
      {children}
    </ReplaceDecimalRoundingContext.Provider>
  )
}

export const useReplaceDecimalRounding = (): ReplaceDecimalRoundingContextValue => {
  const context = useContext(ReplaceDecimalRoundingContext)
  if (!context) {
    throw new Error('useReplaceDecimalRounding must be used within ReplaceDecimalRoundingProvider')
  }
  return context
}
